import React, { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import ajaxService from "../../../services/ajax-service";
import moment from "moment";
import { calculateAvailableWidth, calculateModalWidth } from "../../../utils/widthCalculator";
import FlagSelector, { getEmojiFlag } from "../../../components/FlagSelector";
import FlagDisplay from "../../../components/FlagDisplay";
import {
  Button,
  Card,
  Popconfirm,
  message,
  Space,
  Breadcrumb,
  Tag,
  Descriptions,
  Spin,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Switch,
  Typography,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
} from "@ant-design/icons";

const { Title } = Typography;
// Title component for modal header

const OriginCountryDetails = () => {
  const { id } = useParams();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [cardWidth, setCardWidth] = useState('100%');
  const [modalWidth, setModalWidth] = useState('35%');

  const [showEditModal, setShowEditModal] = useState(false);
  const [form] = Form.useForm();
  const countryNameValue = Form.useWatch('name', form);

  const fetchData = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await ajaxService.get(`/origin-countries/${id}`);
      if (response.success) {
        setData(response.data);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError("Error fetching data");
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      const response = await ajaxService.delete("/origin-countries/", id);
      if (response.success) {
        message.success("Origin country deleted successfully");
        history.push("/origin-countries");
      } else {
        message.error("Failed to delete origin country");
      }
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Error deleting origin country");
    }
  }, [id, history]);

  const openEditModal = () => {
    form.setFieldsValue({
      name: data.name,
      iso_3166_alpha2: data.iso_3166_alpha2,
      status: data.status,
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (values) => {
    setLoading(true);
    const payload = {
      name: values.name,
      flag: getEmojiFlag(values.iso_3166_alpha2),
      iso_3166_alpha2: values.iso_3166_alpha2,
      status: values.status ? 1 : 0,
    };

    try {
      const response = await ajaxService.put("/origin-countries/", id, payload);
      if (response.success) {
        message.success("Origin country updated successfully");
        setData(response.data);
        fetchData(id);
        setShowEditModal(false);
      } else {
        message.error("Failed to update origin country");
      }
    } catch (err) {
      console.error("Update error:", err);
      message.error("Error updating origin country");
    }
  };

  useEffect(() => {
    if (id) fetchData(id);
  }, [id, fetchData]);

  useEffect(() => {
    const updateWidth = () => {
      const width = calculateAvailableWidth(sidebarCollapsed, 40);
      setCardWidth(width);
      setModalWidth(calculateModalWidth('35%'));
    };
    
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
      updateWidth();
    };
    
    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    window.addEventListener('resize', updateWidth);
    updateWidth();
    
    return () => {
      window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
      window.removeEventListener('resize', updateWidth);
    };
  }, [sidebarCollapsed]);

  if (loading) {
    return (
      <div className="page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', padding: 'var(--spacing-2xl)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--error-red)' }}>{error}</p>
    </div>
  );
  if (!data) return (
    <div className="page-container" style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-gray)' }}>No data found</p>
    </div>
  );

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="module-header">
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <Space>
            <Button
              type="primary"
              ghost
              icon={<EditOutlined />}
              onClick={openEditModal}
              style={{
                background: 'var(--primary-purple)',
                borderColor: 'var(--primary-purple)',
                color: 'white',
              }}
            >
              Edit
            </Button>

            <Popconfirm
              title="Are you sure to delete this origin country?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                style={{
                  background: 'var(--error-red)',
                  borderColor: 'var(--error-red)',
                }}
              >
                Delete
              </Button>
            </Popconfirm>

            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push("/origin-countries")}
              style={{
                background: 'var(--dark-bg-card)',
                borderColor: 'rgba(254, 2, 125, 0.3)',
                color: 'var(--text-white)',
              }}
            >
              Back
            </Button>
          </Space>
        </div>
        <h1 className="module-title">Origin Country Details</h1>
        <p className="module-subtitle">View and manage origin country information</p>
      </div>

      <Card 
        style={{
          width: typeof cardWidth === 'string' ? cardWidth : `${cardWidth}px`,
          maxWidth: '100%',
          boxShadow: 'var(--shadow-card)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--dark-bg-card)',
          border: '1px solid rgba(254, 2, 125, 0.2)',
        }}
      >
        <Descriptions
          bordered
          column={1}
          labelStyle={{ fontWeight: "bold", width: "200px", color: 'var(--text-white)' }}
        >
          <Descriptions.Item label="ID"><span style={{ color: 'var(--text-white)' }}>{data.id}</span></Descriptions.Item>
          <Descriptions.Item label="Flag">
            <FlagDisplay flag={data.flag} isoCode={data.iso_3166_alpha2} size={40} />
          </Descriptions.Item>
          <Descriptions.Item label="Country Name">
            <span style={{ color: 'var(--text-white)' }}>{data.name || "N/A"}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={data.status ? "green" : "red"}>
              {data.status ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.created_at
                ? moment(data.created_at).format("YYYY-MM-DD")
                : "N/A"}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            <span style={{ color: 'var(--text-white)' }}>
              {data.updated_at
                ? moment(data.updated_at).format("YYYY-MM-DD")
                : "N/A"}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Modal
        open={showEditModal}
        onCancel={() => setShowEditModal(false)}
        footer={null}
        className="modal-overlay"
        width={modalWidth}
        centered
      >
        <div style={{
          padding: "var(--spacing-2xl) var(--spacing-xl)",
          background: 'var(--dark-bg-card)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <div style={{
            marginBottom: 'var(--spacing-2xl)',
            textAlign: "center",
            paddingBottom: 'var(--spacing-lg)',
            borderBottom: '1px solid rgba(254, 2, 125, 0.2)'
          }}>
            <Title level={3} style={{
              color: 'var(--text-white)',
              margin: 0,
              fontFamily: 'Poppins-Bold',
              fontSize: '1.5rem'
            }}>
              Edit Origin Country
            </Title>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditSubmit}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label={<span style={{
                    color: 'var(--text-white)',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    display: 'block',
                    marginBottom: 'var(--spacing-xs)'
                  }}>Country Name</span>}
                  name="name"
                  rules={[{ required: true, message: "Country name is required" }]}
                >
                  <Input
                    placeholder="Enter country name"
                    allowClear
                    className="input-field"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label={<span style={{
                    color: 'var(--text-white)',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    display: 'block',
                    marginBottom: 'var(--spacing-xs)'
                  }}>Country Flag</span>}
                  name="iso_3166_alpha2"
                >
                  <FlagSelector
                    countryName={countryNameValue}
                    onSelect={(country) => {
                      form.setFieldsValue({
                        iso_3166_alpha2: country.code
                      });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  label={<span style={{
                    color: 'var(--text-white)',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    display: 'block',
                    marginBottom: 'var(--spacing-xs)'
                  }}>Status</span>}
                  name="status"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                    style={{ backgroundColor: 'var(--primary-purple)' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{
              textAlign: "center",
              marginTop: 'var(--spacing-2xl)',
              paddingTop: 'var(--spacing-lg)',
              borderTop: '1px solid rgba(254, 2, 125, 0.2)',
              display: 'flex',
              gap: 'var(--spacing-md)',
              justifyContent: 'center'
            }}>
              <Button
                onClick={() => setShowEditModal(false)}
                style={{
                  minWidth: '120px',
                  height: '40px',
                  background: 'var(--dark-bg-hover)',
                  borderColor: 'rgba(254, 2, 125, 0.3)',
                  color: 'var(--text-white)'
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={form.submit}
                className="btn-primary"
                style={{
                  minWidth: '120px',
                  height: '40px'
                }}
              >
                Update
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default OriginCountryDetails;
